/**
 * Wellness Check-In Modal
 * Daily wellness check-in flow for Aira
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Chip,
  TextField,
  LinearProgress,
  Alert,
  IconButton,
  Stack
} from '@mui/material';
import {
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import * as wellnessAPI from '../../services/api/wellnessAPI';
import apiClient from '../../services/api/config';
import toast from 'react-hot-toast';

const MoodButton = styled(Button)<{ selected?: boolean }>(({ theme, selected }) => ({
  minWidth: 80,
  minHeight: 80,
  flexDirection: 'column',
  gap: theme.spacing(1),
  borderRadius: '16px',
  border: selected ? `3px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.divider}`,
  backgroundColor: selected ? 'rgba(192, 235, 56, 0.15)' : 'transparent',
  '&:hover': {
    backgroundColor: selected ? 'rgba(192, 235, 56, 0.2)' : theme.palette.action.hover,
  }
}));

const StateChip = styled(Chip)<{ selected?: boolean }>(({ theme, selected }) => ({
  margin: theme.spacing(0.5),
  borderRadius: '16px',
  borderWidth: selected ? 2 : 1,
  borderColor: selected ? theme.palette.primary.main : theme.palette.divider,
  backgroundColor: selected ? 'rgba(192, 235, 56, 0.15)' : 'transparent',
  fontWeight: selected ? 600 : 400,
  '&:hover': {
    backgroundColor: selected ? 'rgba(192, 235, 56, 0.2)' : 'rgba(0, 0, 0, 0.04)',
  }
}));

interface WellnessCheckInModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (suggestions: string[]) => void;
}

const MOOD_OPTIONS = [
  { value: 'very_negative', label: 'Very Bad', icon: SentimentVeryDissatisfied, color: '#f44336' },
  { value: 'negative', label: 'Bad', icon: SentimentDissatisfied, color: '#ff9800' },
  { value: 'neutral', label: 'Okay', icon: SentimentNeutral, color: '#9e9e9e' },
  { value: 'positive', label: 'Good', icon: SentimentSatisfied, color: '#4caf50' },
  { value: 'very_positive', label: 'Great', icon: SentimentVerySatisfied, color: '#2196f3' }
];

const STATE_OPTIONS = [
  'Stressed', 'Tired', 'Focused', 'Overwhelmed', 'Motivated',
  'Anxious', 'Calm', 'Energized', 'Frustrated', 'Content',
  'Burned out', 'Inspired', 'Confused', 'Confident', 'Worried'
];

const HELP_OPTIONS = [
  'Take a break', 'Talk to someone', 'Exercise', 'Meditation',
  'Better workload management', 'More sleep', 'Healthy eating',
  'Time off', 'Learning something new', 'Social connection'
];

export default function WellnessCheckInModal({ open, onClose, onComplete }: WellnessCheckInModalProps) {
  const [step, setStep] = useState(1);
  const [mood, setMood] = useState<string>('');
  const [states, setStates] = useState<string[]>([]);
  const [helps, setHelps] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [airaResponse, setAiraResponse] = useState<string>('');
  const [copingTechniques, setCopingTechniques] = useState<Array<{title: string; description: string; action: string}>>([]);

  const [loadingTechniques, setLoadingTechniques] = useState(false);

  const handleMoodSelect = (value: string) => {
    setMood(value);
    
    const responses: Record<string, string> = {
      'very_negative': "I'm sorry you're having a really tough day. Remember, it's okay to not be okay. Let's explore what might help you feel better.",
      'negative': "I hear you - today has been challenging. Let's work together to find some ways to support you through this.",
      'neutral': "Thanks for checking in. Even on okay days, it's good to take a moment for yourself. Let's see what might brighten your day.",
      'positive': "That's wonderful to hear! Let's build on this positive energy and see what's working well for you.",
      'very_positive': "I'm so glad you're feeling great! Let's capture what's contributing to this positive state so we can maintain it."
    };
    
    setAiraResponse(responses[value] || '');
    
    generateCopingTechniques(value, states);
  };

  const generateCopingTechniques = async (moodValue: string, currentStates: string[]) => {
    setLoadingTechniques(true);
    try {
      const response = await apiClient.post('/ai/wellness-techniques', {
        mood: moodValue,
        states: currentStates,
      });
      if (response.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
        setCopingTechniques(response.data.data);
        return;
      }
    } catch (err) {
      console.error('AI techniques error, using fallbacks:', err);
    } finally {
      setLoadingTechniques(false);
    }
    const fallback: Record<string, Array<{title: string; description: string; action: string}>> = {
      'very_negative': [
        { title: '5-4-3-2-1 Grounding Exercise', description: 'A quick technique to help you feel present and calm when overwhelmed.', action: 'Name 5 things you see, 4 you can touch, 3 you hear, 2 you smell, and 1 you taste.' },
        { title: 'Reach Out for Support', description: 'Talking to someone can make a big difference.', action: 'Consider reaching out to a trusted colleague, friend, or mental health professional.' },
        { title: 'Take a Mental Health Break', description: 'Sometimes stepping away is the best thing you can do.', action: 'Take 15-30 minutes away from work. Go for a walk, listen to music, or do something you enjoy.' }
      ],
      'negative': [
        { title: 'Box Breathing Exercise', description: 'A simple breathing technique to reduce stress and anxiety.', action: 'Breathe in for 4 counts, hold for 4, exhale for 4, hold for 4. Repeat 4 times.' },
        { title: 'Prioritize Your Tasks', description: 'Feeling overwhelmed? Break it down.', action: 'List your top 3 priorities for today. Focus on one at a time.' },
        { title: 'Physical Movement', description: 'Movement can shift your mental state.', action: 'Take a 10-minute walk, do some stretches, or try a quick workout.' }
      ],
      'neutral': [
        { title: 'Gratitude Practice', description: 'Shift your focus to the positive.', action: 'Write down 3 things you are grateful for today, no matter how small.' },
        { title: 'Mindful Moment', description: 'Take a pause to be present.', action: 'Spend 5 minutes in quiet reflection or meditation.' },
        { title: 'Connect with Others', description: 'Social connection boosts wellbeing.', action: 'Have a meaningful conversation with a colleague or friend.' }
      ],
      'positive': [
        { title: 'Capture the Moment', description: 'Document what is working well.', action: 'Journal about what contributed to your positive mood today.' },
        { title: 'Share the Positivity', description: 'Spread the good vibes.', action: 'Compliment a colleague or share something positive with your team.' },
        { title: 'Build on Success', description: 'Use this energy productively.', action: 'Tackle a challenging task while you have this positive momentum.' }
      ],
      'very_positive': [
        { title: 'Celebrate Your Wins', description: 'Acknowledge your achievements.', action: 'Take a moment to recognize what you have accomplished and how far you have come.' },
        { title: 'Pay It Forward', description: 'Help others feel good too.', action: 'Offer support to a colleague or mentor someone who could use guidance.' },
        { title: 'Set New Goals', description: 'Channel this energy into growth.', action: 'Think about what you want to achieve next and set one actionable goal.' }
      ]
    };
    setCopingTechniques(fallback[moodValue] || []);
  };

  const handleStateToggle = (state: string) => {
    setStates(prev =>
      prev.includes(state)
        ? prev.filter(s => s !== state)
        : [...prev, state]
    );
  };

  const handleHelpToggle = (help: string) => {
    setHelps(prev =>
      prev.includes(help)
        ? prev.filter(h => h !== help)
        : [...prev, help]
    );
  };

  const handleNext = () => {
    if (step === 1 && !mood) {
      toast.error('Please select how you\'re feeling');
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (states.length === 0) {
      toast.error('Please select at least one state');
      return;
    }

    setIsSubmitting(true);

    try {
      const answers = [
        {
          questionId: 'q1',
          prompt: 'How are you feeling today?',
          choice: mood,
          response: MOOD_OPTIONS.find(m => m.value === mood)?.label || mood
        },
        {
          questionId: 'q2',
          prompt: 'What best describes your current state?',
          choice: states.join(', '),
          response: states.join(', ')
        },
        {
          questionId: 'q3',
          prompt: 'What\'s one thing that could help today?',
          choice: helps.join(', '),
          response: helps.join(', ') + (notes ? ` - ${notes}` : '')
        }
      ];

      const result = await wellnessAPI.submitCheckIn({
        mood,
        answers,
        source: 'aira-daily'
      });

      toast.success('Wellness check-in completed!');
      onComplete(result.suggestions);
      handleClose();
    } catch (error) {
      console.error('Error submitting wellness check-in:', error);
      toast.error('Failed to submit wellness check-in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setMood('');
    setStates([]);
    setHelps([]);
    setNotes('');
    setAiraResponse('');
    setCopingTechniques([]);
    onClose();
  };

  const progress = (step / 2) * 100;

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Prevent closing without completing
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)'
          }
        }
      }}
            PaperProps={{
              elevation: 24,
              sx: {
                borderRadius: '32px',
                backgroundColor: '#ffffff',
                backgroundImage: 'linear-gradient(135deg, #E8F4FD 0%, #F0F4F8 50%, #FFFFFF 100%)',
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
              }
            }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {step > 1 && (
            <IconButton size="small" onClick={handleBack} disabled={isSubmitting}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" fontWeight={600}>
            Daily Wellness Check-In
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{ mt: 2, borderRadius: 1 }} />
      </DialogTitle>

      <DialogContent>
        <Box py={2}>
          {step === 1 && (
            <Box>
              <Typography variant="h5" gutterBottom textAlign="center" fontWeight={500}>
                How are you feeling today?
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                Select the option that best describes your mood
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                {MOOD_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <MoodButton
                      key={option.value}
                      selected={mood === option.value}
                      onClick={() => handleMoodSelect(option.value)}
                    >
                      <Icon sx={{ fontSize: 40, color: option.color }} />
                      <Typography variant="caption">{option.label}</Typography>
                    </MoodButton>
                  );
                })}
              </Stack>
              
              {airaResponse && (
                <Alert 
                  severity={mood === 'very_negative' || mood === 'negative' ? 'warning' : 'info'} 
                  sx={{ mt: 3 }}
                  icon={false}
                >
                  <Typography variant="body2" fontWeight={500} gutterBottom>
                    💙 Aira says:
                  </Typography>
                  <Typography variant="body2">
                    {airaResponse}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {step === 2 && (
            <Box>
              <Typography variant="h5" gutterBottom textAlign="center" fontWeight={500}>
                What best describes your current state?
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                Select all that apply
              </Typography>
              <Box display="flex" flexWrap="wrap" justifyContent="center">
                {STATE_OPTIONS.map((state) => (
                  <StateChip
                    key={state}
                    label={state}
                    selected={states.includes(state)}
                    onClick={() => handleStateToggle(state)}
                    clickable
                  />
                ))}
              </Box>
            </Box>
          )}

          {step === 1 && (
            <Alert severity="info" sx={{ mt: 3 }}>
              This daily check-in helps us understand your wellbeing and provide personalized support.
              Your responses are confidential.
            </Alert>
          )}
        </Box>
      </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'flex-end' }}>
              {step < 2 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={isSubmitting}
                  size="large"
                  sx={{ borderRadius: '16px', bgcolor: 'primary.main', color: '#1A1D1F', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  size="large"
                  startIcon={isSubmitting ? null : <CheckCircleIcon />}
                  sx={{ borderRadius: '16px', bgcolor: 'primary.main', color: '#1A1D1F', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  {isSubmitting ? 'Submitting...' : 'Complete Check-In'}
                </Button>
              )}
            </DialogActions>
    </Dialog>
  );
}
